import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

// L4.08 — Workflow instance grid (search by business reference).
// GET /v1/wf/instances?status=&businessRef=&limit=&offset= → { rows, total }

interface WfInstance {
  readonly instanceId: string;
  readonly instanceRefNumber: string;
  readonly entityType: string;
  readonly entityRefNumber: string | null;
  readonly initiatorUserId: string;
  readonly initiatorName: string | null;
  readonly initiatorLoginId: string | null;
  readonly workflowCode: string | null;
  readonly workflowName: string | null;
  readonly instanceStatus: string;
  readonly submittedAt: string | null;
}

interface WfInstanceRef {
  readonly businessRef: string;
  readonly entityType: string;
}

const PAGE = 25;
const STATUSES = ['', 'DRAFT', 'IN_PROGRESS', 'APPROVED', 'REJECTED', 'RECALLED', 'ON_HOLD', 'CANCELLED'] as const;

export function WorkflowInstanceListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<WfInstance[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [businessRef, setBusinessRef] = useState('');
  const [search, setSearch] = useState('');
  const [refOptions, setRefOptions] = useState<WfInstanceRef[]>([]);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (search) params.set('businessRef', search);
      params.set('limit', String(PAGE));
      params.set('offset', String(offset));
      const r = await apiRequest<{ rows: WfInstance[]; total: number }>(
        `/v1/wf/instances?${params.toString()}`, { token },
      );
      setRows(r.rows); setTotal(r.total); setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [status, search, offset, token]);

  useEffect(() => { void load(); }, [load]);

  // The list of references is driven by the status filter so the box never
  // offers a reference that the current filter would then hide.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const q = status ? `?status=${encodeURIComponent(status)}` : '';
        const r = await apiRequest<{ rows: WfInstanceRef[] }>(
          `/v1/wf/instance-refs${q}`, { token },
        );
        if (!cancelled) setRefOptions(r.rows);
      } catch { if (!cancelled) setRefOptions([]); }
    })();
    return () => { cancelled = true; };
  }, [status, token]);

  return (
    <main aria-label="wf-instances">
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('wf.instList.title')}</h1>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="inst-status" style={{ fontSize: 12 }}>{t('wf.common.status')}</label>
          <select id="inst-status" aria-label="status-filter" value={status}
                  onChange={(e) => { setOffset(0); setStatus(e.target.value); }}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('wf.common.all')}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="inst-ref" style={{ fontSize: 12 }}>{t('wf.instList.businessRefLabel')}</label>
          <select id="inst-ref" aria-label="business-ref" value={businessRef}
                  onChange={(e) => { const v = e.target.value; setBusinessRef(v); setOffset(0); setSearch(v); }}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)', minWidth: 220 }}>
            <option value="">{t('wf.instList.refAll')}</option>
            {refOptions.map((o) => (
              <option key={o.businessRef} value={o.businessRef}>{o.businessRef}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="instances-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.instList.empty')}</p>
      ) : (
        <>
          <DataTable<WfInstance>
            ariaLabel="instances-table"
            rows={rows}
            rowKey={(inst) => inst.instanceId}
            rowTestId={(inst) => `instance-row-${inst.instanceId}`}
            columns={[
              { key: 'ref', header: t('wf.instList.colRef'), render: (inst) => <Link to={`/admin/wf/instances/${inst.instanceId}`}>{inst.instanceRefNumber}</Link> },
              { key: 'entity', header: t('wf.common.entity'), render: (inst) => inst.entityType },
              { key: 'businessRef', header: t('wf.instList.colBusinessRef'), render: (inst) => inst.entityRefNumber ?? '—' },
              {
                key: 'workflow',
                header: t('wf.instList.colWorkflow'),
                render: (inst) => inst.workflowName ?? inst.workflowCode ?? '—',
              },
              {
                key: 'initiator',
                header: t('wf.instList.colInitiator'),
                // The raw uuid is kept only as a fallback: a grid that names
                // nobody cannot be read by the people who use it.
                render: (inst) => (inst.initiatorName
                  ? `${inst.initiatorName}${inst.initiatorLoginId ? ` (${inst.initiatorLoginId})` : ''}`
                  : inst.initiatorUserId),
              },
              { key: 'status', header: t('wf.common.status'), render: (inst) => inst.instanceStatus },
              { key: 'submitted', header: t('wf.common.submitted'), render: (inst) => (inst.submittedAt ? at(inst.submittedAt) : '—') },
            ]}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <Button type="button" variant="secondary" data-testid="prev-page" disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE))}>{t('wf.instList.prev')}</Button>
            <span style={{ fontSize: 13 }}>{t('wf.instList.pageRange', { from: offset + 1, to: Math.min(offset + PAGE, total), total })}</span>
            <Button type="button" variant="secondary" data-testid="next-page" disabled={offset + PAGE >= total}
                    onClick={() => setOffset((o) => o + PAGE)}>{t('wf.instList.next')}</Button>
          </div>
        </>
      )}
    </main>
  );
}
